import { Component } from '@ribajs/core'
import { SbCartComponent } from './sb-cart.component.js'
import {
  ShopifyCartService,
  ShopifyProductService,
  ToasterService,
} from '../services/index.js'

import type {
  ShopifyCartLineItem,
  ShopifyProductVariant,
  ShopifyProduct,
} from '../types/index.js'
interface Scope {
  /** The line item */
  item?: ShopifyCartLineItem
  /** The product variant associated with the line item */
  variant?: ShopifyProductVariant
  /** The product associated with the line item */
  product?: ShopifyProduct

  // METHODS

  removeItem: SbCartLineItemComponent['removeItem']
  increaseItem: SbCartLineItemComponent['increaseItem']
  decreaseItem: SbCartLineItemComponent['decreaseItem']
}

/**
 * Component representing a single line item in the shopping cart.
 * Handles operations such as removing, increasing, and decreasing item quantities.
 */
export class SbCartLineItemComponent extends Component {
  public static tagName = 'sb-cart-line-item'

  parent?: SbCartComponent

  protected toasterService = ToasterService.getSingleton()

  public scope: Scope = {
    item: undefined,
    variant: undefined,
    product: undefined,
    removeItem: this.removeItem.bind(this),
    increaseItem: this.increaseItem.bind(this),
    decreaseItem: this.decreaseItem.bind(this),
  }

  static get observedAttributes(): string[] {
    return ['item']
  }

  protected requiredAttributes = (): string[] => ['item']

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCartLineItemComponent.observedAttributes)
  }

  protected async beforeBind() {
    await super.beforeBind()
    this.parent =
      this.closest<SbCartComponent>(SbCartComponent.tagName) || undefined
    if (!this.parent) {
      throw new Error('sb-cart-line-item must be a child of sb-cart')
    }

    this.prepareProductVariant()
  }

  /**
   * Called after the component is bound to the DOM.
   * Adds a ready class and calls the superclass's afterBind method.
   */
  protected async afterBind() {
    this.debug('afterBind', this.scope)
    this.classList.add(`${SbCartLineItemComponent.tagName}-ready`)
    await super.afterBind()
  }

  /**
   * Prepares the product variant for the line item.
   * Retrieves variant data from Shopify and sets the background color if available.
   */
  protected async prepareProductVariant() {
    const lineItem = this.scope.item!
    const { metafields, product, bgColor, variant } =
      await ShopifyProductService.getPreparedProductData(
        lineItem.handle,
        lineItem.variant_id,
      )
    if (!variant) {
      throw new Error('No variant found for line item', { cause: lineItem })
    }

    await ShopifyProductService.getPreparedProductVariantData(
      product,
      variant,
      metafields,
    )
    this.scope.variant = variant
    this.scope.product = product
    if (bgColor) ShopifyProductService.setImageBgColor(this, bgColor)
  }

  /**
   * Removes the item from the cart.
   * Updates the local cart state and sends a request to Shopify to update the cart.
   */
  public async removeItem() {
    const lineItem = this.scope.item!
    this.parent!.pending = true
    try {
      // For faster response, we do not wait for the Shopify response to remove the item from the cart
      this.parent!.removeLocalItem(lineItem)

      await ShopifyCartService.change(lineItem.key, 0)
    } catch (error) {
      this.error(error)
    } finally {
      this.parent!.pending = false
    }
  }

  /**
   * Updates the line item quantity and sends request to Shopify
   */
  protected async updateLineItemQuantity(
    lineItem: ShopifyCartLineItem,
    newQuantity: number,
  ): Promise<void> {
    const originalQuantity = lineItem.quantity
    lineItem.quantity = newQuantity
    this.updatePrice(lineItem)

    try {
      await ShopifyCartService.change(lineItem.key, lineItem.quantity)
    } catch (error) {
      // Revert on error
      lineItem.quantity = originalQuantity
      this.updatePrice(lineItem)
      this.error(error)
    }
  }

  /**
   * Updates the line item quantity by the given change amount
   * @param change - The amount to change the quantity by (positive or negative)
   */
  protected async updateItemQuantity(change: number): Promise<void> {
    const lineItem = this.scope.item!
    const variant = this.scope.variant!
    const originalQuantity = lineItem.quantity
    this.parent!.pending = true

    try {
      const { increment } = ShopifyProductService.getQuantityRule(variant)
      const nextQuantity = lineItem.quantity + change * increment

      // Validate the new quantity
      const validateQuantityResult = ShopifyCartService.validateQuantity(
        variant,
        nextQuantity,
        {
          excludeLineItemKeys: [lineItem.key],
        },
      )

      if (validateQuantityResult.isValid) {
        await this.updateLineItemQuantity(
          lineItem,
          validateQuantityResult.allowedUserInput,
        )
      } else {
        await ShopifyProductService.showQuantityValidationError(
          validateQuantityResult,
        )
        lineItem.quantity = validateQuantityResult.allowedUserInput
        this.updatePrice(lineItem)
      }
    } catch (error) {
      this.error(error)
      lineItem.quantity = originalQuantity
      this.updatePrice(lineItem)
    } finally {
      this.parent!.pending = false
    }
  }

  /**
   * Increases the quantity of the line item.
   * Updates the local state and sends a request to Shopify to update the cart.
   */
  public async increaseItem(): Promise<void> {
    await this.updateItemQuantity(1)
  }

  /**
   * Decreases the quantity of the line item.
   * Uses the same validation logic as increaseItem.
   */
  public async decreaseItem(): Promise<void> {
    await this.updateItemQuantity(-1)
  }

  /**
   * Updates the price of the line item based on its quantity.
   * @param lineItem The line item to update.
   */
  protected updatePrice(lineItem: any) {
    lineItem.final_line_price = lineItem.final_price * lineItem.quantity
    lineItem.original_line_price = lineItem.original_price * lineItem.quantity
  }

  protected template() {
    return null
  }
}
